import { useContext, useMemo } from 'react';
import { sortBy } from 'lodash';
import { Button } from 'reactstrap';
import styled from 'styled-components';
import {
  DEFAULT_IN_ROW_SPACING,
  headlandSectionTypes,
  sectionTypes,
  UNKNOWN_CROP,
} from '../../constants/fields';
import { MapContext } from '../../contexts/MapContext';
import { getFarmMeta } from '../../utils/meta';
import {
  AREA_UNITS,
  DISTANCE_UNITS,
  INVERTED_STEM_AREA_UNITS,
  ROW_UNITS,
} from '../../constants/units';
import { N_YEARS } from '../../constants/years';
import {
  asDynamicDecimal,
  asQuantity,
  asRoundedDecimal,
  unitForValue,
} from '../../utils/formatters';
import { useFarmName, useToggleState, useYearsArray } from '../../utils/hooks';
import * as normalizers from '../../utils/normalizers';
import * as validators from '../../utils/validators';
import CropSelector from '../CropSelector';
import { PanelMainGroup, PanelSection } from '../SidePanel';
import { YearPopover } from '../YearSelect';
import IconButton from '../basics/IconButton';
import BetweenRowIcon from '../icons/BetweenRowIcon';
import DegreeIcon from '../icons/DegreeIcon';
import InRowIcon from '../icons/InRowIcon';
import RowCountIcon from '../icons/RowCountIcon';
import RightMenuTabs from './RightMenuTabs';
import ExportButtons from './export/ExportButtons';
import {
  CropSwatchPicker,
  CropVarietyDropdown,
  LabeledData,
  LabeledInput,
  SectionHiddenWarning,
} from './menu-components';
import { sectionHasGeodata } from '../../utils/sections';
import RowOrderToggles from './RowOrderToggles';
import { useUnassignedStemsContext } from '../../contexts/UnassignedStemsContext';
import UnassignedStemsSwitch from './UnassignedStemsSwitch';
import PatternListDropdown from '../PatternListDropdown';
import OffsetInput from './OffsetInput';
import { mapModes } from '../../constants/map';
import ChevronIcon from '../icons/ChevronIcon';
import { captureEvent } from '../../utils/analytics';
import SectionNotes from './notes/SectionNotes';
import SelectedPatternDropdown from './SelectedPatternDropdown';
import CropVarietySelector from '../CropVarietySelector';
import { FarmCropData } from './CropData';
import {
  rightMenuTabs,
  useRightMenuTabContext,
} from '../../contexts/RightMenuTabContext';
import { PopOutTabContainer } from './PopOutTab';
import HeadlandsIcon from '../icons/HeadlandsIcon';

const FlipButton = styled(Button)`
  font-size: 1em;
  margin: 0 0 4px 4px;
  padding: 0 4px;
`;

const DesignTab = ({ section, readonly, colors, cropData }) => {
  const {
    crops: { availableCrops: cropMap, updateCropColor },
    fields: { updateFieldSection },
    mapState: { mapMode },
    patterns: { inRowPatternsMap },
  } = useContext(MapContext);
  const farmName = useFarmName();

  // section.startYear is an absolute year (i.e. 2021)
  // search through yearsArray (what's used to populate the YearSelect)
  // to get the index
  const allYears = useYearsArray(N_YEARS);
  const sectionYearIndex = useMemo(() => {
    const index = allYears.findIndex((val) => val === section.startYear);
    return index !== -1 ? index : 0;
  }, [allYears, section]);

  const primaryCrop = section.crop?.id
    ? cropMap[section.crop?.id]
    : UNKNOWN_CROP;

  const inRowPattern = section.primaryInRowPattern?.id
    ? inRowPatternsMap[section.primaryInRowPattern.id]
    : null;

  const showRowOrderToggles =
    section.type !== sectionTypes.IMPORTED && !readonly && section.rows;
  const showOffsetInput = section.type === sectionTypes.GRID;
  const showOffsetInputAboveFold =
    mapMode === mapModes.OFFSET || section.offsetX || section.offsetY;

  const [isOpen, toggleOpen] = useToggleState();
  const [varietyIsOpen, toggleVarietyOpen] = useToggleState(
    !!section.primaryCropVariety,
  );

  const onSelectPattern = (pattern) => {
    captureEvent('pattern-added-to-section');
    updateFieldSection(section.id, {
      primaryInRowPattern: pattern,
      crop: null,
      inRowSpacing: null,
      primaryCropVariety: null,
    });
  };

  return (
    <>
      <PanelSection>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h4 className="m-0">Geometry</h4>
          {!readonly && (
            <PatternListDropdown onSelectPattern={onSelectPattern} />
          )}
        </div>
        {section.type !== sectionTypes.IMPORTED && (
          <LabeledInput
            label="between row"
            unitSpec={DISTANCE_UNITS}
            readonly={readonly || section.type === sectionTypes.IMPORTED}
            update={(value) =>
              updateFieldSection(section.id, {
                betweenRowSpacing: value,
              })
            }
            normalize={normalizers.asFloat}
            isValid={validators.isPositiveNumber}
            type="number"
            min={1}
            step="any"
            defaultValue={section.betweenRowSpacing}
            formatter={asRoundedDecimal}
            icon={<BetweenRowIcon />}
          />
        )}
        {!section.primaryInRowPattern && (
          <LabeledInput
            label="in row"
            unitSpec={DISTANCE_UNITS}
            readonly={readonly}
            update={(value) =>
              updateFieldSection(section.id, {
                inRowSpacing: value,
              })
            }
            normalize={normalizers.asFloat}
            isValid={validators.isPositiveNumber}
            type="number"
            min={1}
            step="any"
            defaultValue={section.inRowSpacing}
            formatter={asRoundedDecimal}
            icon={<InRowIcon />}
          />
        )}
        {headlandSectionTypes.includes(section.type) && (
          <div className="d-flex align-items-center justify-content-between">
            <LabeledInput
              defaultValue={section.headlandWidth ?? 0}
              icon={<HeadlandsIcon />}
              isValid={validators.isNonNegativeNumber}
              label="headland"
              style={
                readonly || !section.headlandWidth
                  ? undefined
                  : { maxWidth: '56px' }
              }
              min={0}
              normalize={normalizers.asFloat}
              readonly={readonly}
              step="any"
              type="number"
              unitSpec={DISTANCE_UNITS}
              update={(value) =>
                updateFieldSection(section.id, {
                  headlandWidth: value,
                })
              }
            />
            {!readonly && !!section.headlandWidth && (
              <FlipButton
                onClick={() =>
                  updateFieldSection(section.id, {
                    headlandInnerFeature: !section.headlandInnerFeature,
                  })
                }
              >
                Flip {section.headlandInnerFeature ? 'Inside' : 'Outside'}
              </FlipButton>
            )}
          </div>
        )}
        {section.type === sectionTypes.GRID && (
          <LabeledInput
            label="rotation"
            unit="°"
            readonly={readonly}
            normalize={normalizers.asRotationDegrees}
            isValid={validators.isNumber}
            type="number"
            step={0.5}
            update={(value) =>
              updateFieldSection(section.id, {
                rotation: value,
              })
            }
            defaultValue={section.rotation}
            icon={<DegreeIcon />}
          />
        )}
        {/* Don't show this for viewers, since it is also shown in the rows section */}
        {section.type === sectionTypes.LINE && !readonly && (
          <LabeledInput
            label="row count"
            readonly={section.type !== sectionTypes.LINE || readonly}
            defaultValue={section.rows?.features.length || 1}
            isValid={validators.isPositiveInteger}
            normalize={normalizers.asInteger}
            type="number"
            min={1}
            step={1}
            update={(value) =>
              updateFieldSection(section.id, {
                inputRowCount: value,
              })
            }
            icon={<RowCountIcon />}
          />
        )}
        {(showOffsetInputAboveFold || (showOffsetInput && isOpen)) && (
          <OffsetInput
            readonly={readonly}
            updateFieldSection={updateFieldSection}
            section={section}
          />
        )}
        {showRowOrderToggles && isOpen && (
          <RowOrderToggles
            section={section}
            updateFieldSection={updateFieldSection}
          />
        )}
        {(showRowOrderToggles ||
          (showOffsetInput && !showOffsetInputAboveFold)) && (
          <IconButton
            className="font-small text-muted p-0 ml-auto"
            color="text"
            size="sm"
            onClick={toggleOpen}
          >
            Show {isOpen ? 'less' : 'more'}{' '}
            <ChevronIcon direction={isOpen ? 'up' : 'down'} />
          </IconButton>
        )}
      </PanelSection>
      {!readonly &&
        (inRowPattern ? (
          <PanelSection>
            <h4>Pattern</h4>
            <LabeledData label="primary">
              <SelectedPatternDropdown
                patternName={inRowPattern.name}
                patternId={inRowPattern.id}
                onSelectPattern={onSelectPattern}
                onRemoveClick={() =>
                  updateFieldSection(section.id, {
                    primaryInRowPattern: null,
                    inRowSpacing: DEFAULT_IN_ROW_SPACING,
                  })
                }
              />
            </LabeledData>
          </PanelSection>
        ) : (
          <PanelSection>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h4 className="m-0">Crop</h4>
              {primaryCrop?.id && (
                <CropVarietyDropdown
                  isOpen={varietyIsOpen}
                  onClick={toggleVarietyOpen}
                />
              )}
            </div>
            <LabeledData label="primary" labelMinWidth={50}>
              <CropSwatchPicker
                crop={primaryCrop}
                colorOptions={colors}
                color="light"
                onSelect={(color) => updateCropColor(primaryCrop, color)}
                readonly={readonly}
              />
              <CropSelector
                selectedCrop={section.crop}
                onCropSelect={(crop) =>
                  updateFieldSection(section.id, {
                    crop,
                    inRowSpacing: crop.inRowSpacing,
                    betweenRowSpacing: crop.betweenRowSpacing,
                    // Clear crop variety if crop changes
                    ...(crop.id !== section.crop?.id && {
                      primaryCropVariety: null,
                    }),
                  })
                }
              />
            </LabeledData>
            {varietyIsOpen && (
              <LabeledData label="variety" labelMinWidth={50}>
                <CropVarietySelector
                  id="primary-crop-variety"
                  key={section.crop?.id}
                  cropId={section.crop?.id}
                  cropVarietyId={section.primaryCropVariety?.id}
                  onCropVarietySelect={(primaryCropVariety) => {
                    captureEvent('crop-variety-select', {
                      action: primaryCropVariety ? 'assign' : 'remove',
                      location: 'section',
                    });
                    updateFieldSection(section.id, {
                      primaryCropVariety,
                    });
                  }}
                />
              </LabeledData>
            )}
          </PanelSection>
        ))}
      <PanelSection>
        <h4>Economics</h4>
        <LabeledData label="start year">
          {readonly ? (
            <div>{allYears[sectionYearIndex]}</div>
          ) : (
            <div>
              <IconButton
                size="sm"
                id="section-year-select"
                color="light"
                className="ml-n2"
              >
                {allYears[sectionYearIndex]}
                <ChevronIcon />
              </IconButton>
              <YearPopover
                onChange={(index) =>
                  updateFieldSection(section.id, {
                    startYear: allYears[index],
                  })
                }
                target="section-year-select"
                allYears={allYears}
              />
            </div>
          )}
        </LabeledData>
      </PanelSection>
      {sectionHasGeodata(section) && (
        <PanelSection>
          <h4>Export</h4>
          <ExportButtons
            cropData={cropData}
            sections={[section]}
            title={[section.name, farmName].join(' - ')}
            className="w-100 text-truncate"
            size="sm"
            outline
            color="secondary"
          >
            Export {section.name}
          </ExportButtons>
        </PanelSection>
      )}
    </>
  );
};

const InspectTab = ({
  readonly,
  colors,
  totals,
  cropData,
  hasUnassignedStems,
}) => {
  const [varietiesIsOpen, toggleVarietiesOpen] = useToggleState();
  const hasCropVarieties = useMemo(
    () => cropData.some((crop) => !!crop.cropSectionVarieties.length),
    [cropData],
  );
  const {
    acreage,
    plantedAcreage,
    rowCount,
    totalRowLength,
    treeCount,
  } = totals;
  const sortedCropData = useMemo(() => sortBy(cropData, 'crop.name'), [
    cropData,
  ]);

  return (
    <>
      {hasUnassignedStems && (
        <PanelSection>
          <UnassignedStemsSwitch />
        </PanelSection>
      )}
      <PanelSection>
        <h4>Section</h4>
        <LabeledData
          copyable
          label="field area"
          formatter={asDynamicDecimal}
          unitSpec={AREA_UNITS}
        >
          {acreage}
        </LabeledData>
        <LabeledData
          copyable
          label="planted area"
          formatter={asDynamicDecimal}
          unitSpec={AREA_UNITS}
        >
          {plantedAcreage}
        </LabeledData>
        <LabeledData
          copyable
          label="stems"
          unit={unitForValue('stems', 'stem')}
        >
          {asQuantity(treeCount)}
        </LabeledData>
        <LabeledData
          copyable
          label="density"
          formatter={asQuantity}
          unitSpec={INVERTED_STEM_AREA_UNITS}
        >
          {/* Prevent division by zero */}
          {plantedAcreage ? treeCount / plantedAcreage : 0}
        </LabeledData>
      </PanelSection>
      <PanelSection>
        <h4>Rows</h4>
        <LabeledData
          copyable
          label="length"
          unitSpec={ROW_UNITS}
          formatter={asDynamicDecimal}
        >
          {totalRowLength}
        </LabeledData>
        <LabeledData
          copyable
          label="qty"
          formatter={asQuantity}
          unit={unitForValue('rows', 'row')}
        >
          {rowCount}
        </LabeledData>
      </PanelSection>
      {cropData.length > 0 && (
        <PanelSection>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h4 className="m-0">Crops</h4>
            {hasCropVarieties && (
              <CropVarietyDropdown
                isOpen={varietiesIsOpen}
                onClick={toggleVarietiesOpen}
                plural
              />
            )}
          </div>
          {sortedCropData.map(
            ({
              crop,
              cropId,
              totalRowLength: cropRowLength,
              treeCount: cropCount,
              acreage: cropAcreage,
              plantedAcreage: cropPlantedAcreage,
              cropSectionVarieties,
            }) => (
              <FarmCropData
                className="mb-2"
                key={cropId}
                crop={crop}
                qty={cropCount}
                acreage={cropAcreage}
                plantedAcreage={cropPlantedAcreage}
                rowLength={cropRowLength}
                showVarieties={varietiesIsOpen}
                varieties={cropSectionVarieties}
                colors={colors}
                readonly={readonly}
              />
            ),
          )}
        </PanelSection>
      )}
    </>
  );
};

const SectionMenu = ({ section, readonly, colors }) => {
  const { activeTab, setActiveTab, popOutTabOpen } = useRightMenuTabContext();
  const {
    crops: { availableCrops: cropMap, cropNameToId, cropVarietyIdToCropId },
    patterns: { inRowPatternsMap },
  } = useContext(MapContext);

  const { includeUnassignedStems } = useUnassignedStemsContext();

  const { totals, cropData, hasUnassignedStems } = getFarmMeta({
    fieldSections: [section],
    cropsById: cropMap,
    cropNameToId,
    cropVarietyIdToCropId,
    inRowPatternsMap,
    includeUnassignedStems,
  });

  return (
    <>
      {!readonly && (
        <RightMenuTabs
          activeTab={activeTab}
          popOutEnabled
          setActiveTab={setActiveTab}
        />
      )}
      {!section.shown && <SectionHiddenWarning />}
      <PanelMainGroup>
        {(popOutTabOpen || activeTab === rightMenuTabs.INSPECT || readonly) && (
          <PopOutTabContainer title={`Inspect - ${section.name}`}>
            <InspectTab
              readonly={readonly}
              colors={colors}
              totals={totals}
              cropData={cropData}
              hasUnassignedStems={hasUnassignedStems}
            />
          </PopOutTabContainer>
        )}
        {(activeTab === rightMenuTabs.DESIGN || readonly) && (
          <DesignTab
            key={section.id}
            section={section}
            readonly={readonly}
            colors={colors}
            cropData={cropData}
          />
        )}
        {sectionHasGeodata(section) && <SectionNotes sectionId={section.id} />}
      </PanelMainGroup>
    </>
  );
};

export default SectionMenu;
